import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Typed wrapper around the `brandingRegenerate` Cloud Function.
 *
 * The caller has already uploaded the source logo bytes to
 *   tenants/{tenantId}/uploads/logo-{timestamp}.{ext}
 * via Firebase Storage. We pass the path; the function reads, sharps it into
 * every required icon variant, writes versioned PNGs back to Storage, and
 * updates branding.iconSet + bumps brandingVersion.
 */

export const BrandingRegenerateInputSchema = z.object({
  tenantId: z.string().min(1),
  /**
   * The operator's uploaded logo. OMIT to have one generated instead — a
   * monogram on this tenant's own primary colour. Mirrors the server schema;
   * making it required here would have blocked the "use a generated icon"
   * path from the client even though the Function accepts it.
   */
  sourcePath: z.string().min(1).optional(),
});
export type BrandingRegenerateInput = z.infer<typeof BrandingRegenerateInputSchema>;

const IconEntrySchema = z.object({
  src: z.string(),
  size: z.number().int().positive(),
  purpose: z.enum(['any', 'maskable', 'monochrome', 'apple-touch']),
  mime: z.string(),
});

export const BrandingRegenerateOutputSchema = z.object({
  brandingVersion: z.number().int().positive(),
  iconSet: z.array(IconEntrySchema),
});
export type BrandingRegenerateOutput = z.infer<typeof BrandingRegenerateOutputSchema>;

const brandingRegenerateFn: HttpsCallable<BrandingRegenerateInput, BrandingRegenerateOutput> =
  httpsCallable(functions, 'brandingRegenerate');

export async function callBrandingRegenerate(
  input: BrandingRegenerateInput,
): Promise<BrandingRegenerateOutput> {
  const parsed = BrandingRegenerateInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('brandingRegenerate.input', parsed.error.flatten());
  }
  const result = await brandingRegenerateFn(parsed.data);
  const out = BrandingRegenerateOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('brandingRegenerate.output', out.error.flatten());
  }
  return out.data;
}
